
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase/client';
import { Shop } from '@/components/dashboard/shops/types';
import { Badge } from '@/components/ui/badge';

interface Domain {
  id: string;
  domain: string;
  status: 'pending' | 'verified' | 'active';
  created_at: string;
}

export const useDomainManagement = (shop: Shop) => {
  const { toast } = useToast();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [dnsCheckResult, setDnsCheckResult] = useState<any>(null);
  const [manualVerification, setManualVerification] = useState(false);

  const fetchDomains = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('shop_id', shop.id);

      if (error) {
        throw error;
      }

      setDomains(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des domaines:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les domaines",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (shop?.id) {
      fetchDomains();
    }
  }, [shop?.id]);

  const addDomain = async () => {
    if (!newDomain) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer un nom de domaine",
        variant: "destructive",
      });
      return;
    }

    let cleanDomain = newDomain.trim();
    cleanDomain = cleanDomain.replace(/^(https?:\/\/)?(www\.)?/i, '');
    cleanDomain = cleanDomain.split('/')[0];

    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(cleanDomain)) {
      toast({
        title: "Format invalide",
        description: "Veuillez entrer un nom de domaine valide (ex: mondomaine.com)",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      const { data: existingDomain } = await supabase
        .from('domains')
        .select('id')
        .eq('domain', cleanDomain);

      if (existingDomain && existingDomain.length > 0) {
        toast({
          title: "Erreur",
          description: "Ce domaine est déjà enregistré",
          variant: "destructive",
        });
        return;
      }

      const { error: insertError, data: insertedDomain } = await supabase
        .from('domains')
        .insert([{ 
          domain: cleanDomain, 
          shop_id: shop.id, 
          status: 'pending' 
        }])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      await addDomainToVercel(insertedDomain.id, cleanDomain);
      
      toast({
        title: "Succès",
        description: "Domaine ajouté. Veuillez configurer votre DNS pour compléter le processus.",
      });

      setNewDomain('');
      fetchDomains();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du domaine:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le domaine",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addDomainToVercel = async (domainId: string, domain: string) => {
    try {
      setIsVerifying(domainId);
      setVerificationError(null);
      setDnsCheckResult(null);
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 
                          'https://eledlxtrcasnxjjarfti.supabase.co';
      
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 
                              'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVsZWRseHRyY2FzbnhqamFyZnRpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDA3MTA4MTksImV4cCI6MjA1NjI4NjgxOX0.dXnNs6hfwolaWcpmONdZitGuwTVp6YTx4rwQh9k1siE';

      console.log(`Ajout du domaine ${domain} à Vercel pour la boutique ${shop.id}...`);
      
      try {
        const response = await fetch(`${supabaseUrl}/functions/v1/add-domain`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify({
            domain,
            shop_id: shop.id
          })
        });
        
        console.log('Réponse du serveur:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Erreur serveur: ${response.status} - ${errorText}`);
        }
        
        const result = await response.json();
        console.log('Résultat de l\'ajout du domaine:', result);

        if (result.error) {
          setVerificationError(result.error);
          throw new Error(result.error);
        }

        toast({
          title: "Succès",
          description: "Domaine ajouté à Vercel avec succès",
        });

        fetchDomains();
      } catch (error) {
        console.error('Erreur lors de l\'appel à l\'Edge Function:', error);
        
        setVerificationError(error instanceof Error ? error.message : "Erreur lors de l'ajout du domaine à Vercel");
        setManualVerification(true);
        throw error;
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du domaine:', error);
      toast({
        title: "Échec de l'ajout à Vercel",
        description: error instanceof Error ? error.message : 
          "Erreur lors de l'ajout du domaine à Vercel. Veuillez configurer votre DNS et vérifier manuellement.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(null);
    }
  };

  const verifyDomain = async (domainId: string, domain: string) => {
    await addDomainToVercel(domainId, domain);
  };

  const checkDomainDNS = async (domain: string) => {
    try {
      const response = await fetch(`https://dns.google/resolve?name=${domain}&type=CNAME`);
      
      if (!response.ok) {
        throw new Error("Erreur lors de la vérification DNS");
      }
      
      const data = await response.json();
      
      if (data.Answer && data.Answer.length > 0) {
        const pointsToScaloo = data.Answer.some((record: any) => 
          record.data && record.data.includes('scaloo.ai'));
          
        if (pointsToScaloo) {
          return { valid: true };
        }
      }
      
      const aResponse = await fetch(`https://dns.google/resolve?name=${domain}&type=A`);
      const aData = await aResponse.json();
      
      return {
        valid: false,
        error: "Configuration DNS incorrecte. Aucun enregistrement CNAME pointant vers scaloo.ai n'a été trouvé.",
        details: { cname: data, a: aData }
      };
    } catch (error) {
      return {
        valid: false,
        error: "Erreur lors de la vérification DNS",
        details: error
      };
    }
  };

  const updateDomainStatus = async (domainId: string, status: 'pending' | 'verified' | 'active') => {
    try {
      const { error } = await supabase
        .from('domains')
        .update({ status })
        .eq('id', domainId);
        
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut du domaine:', error);
      return false;
    }
  };

  const verifyDomainManually = async (domainId: string) => {
    try {
      setIsVerifying(domainId);
      
      const success = await updateDomainStatus(domainId, 'verified');
      
      if (success) {
        toast({
          title: "Vérification manuelle",
          description: "Le domaine a été marqué comme vérifié. La propagation DNS peut prendre jusqu'à 24 heures pour être effective.",
        });
        
        fetchDomains();
      } else {
        throw new Error("Erreur lors de la vérification manuelle");
      }
    } catch (error) {
      console.error('Erreur lors de la vérification manuelle:', error);
      toast({
        title: "Erreur",
        description: "Impossible de vérifier manuellement le domaine",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(null);
      setManualVerification(false);
    }
  };

  const deleteDomain = async (domainId: string) => {
    try {
      setIsDeleting(domainId);
      
      const { error } = await supabase
        .from('domains')
        .delete()
        .eq('id', domainId);

      if (error) {
        throw error;
      }

      toast({
        title: "Succès",
        description: "Domaine supprimé avec succès",
      });

      fetchDomains();
    } catch (error) {
      console.error('Erreur lors de la suppression du domaine:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le domaine",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return <Badge className="bg-green-500">Actif</Badge>;
    } else if (status === 'verified') {
      return <Badge className="bg-blue-500">Vérifié</Badge>;
    } else {
      return <Badge className="bg-yellow-500">En attente</Badge>;
    }
  };

  return {
    domains,
    newDomain,
    setNewDomain,
    isLoading,
    isVerifying,
    isDeleting,
    verificationError,
    dnsCheckResult,
    manualVerification,
    addDomain,
    verifyDomain,
    verifyDomainManually,
    deleteDomain,
    getStatusBadge
  };
};

export default useDomainManagement;
